import React, { useState, useEffect } from 'react';
import './Tasks.css';
import CustomDropdown from '../CustomDropdown';
import AlertModal from '../AlertModal';
import ConfirmModal from '../ConfirmModal';
import Pagination from '../Pagination';
import usePagination from '../../hooks/usePagination';
import { API_BASE_URL } from '../../utils/api';
import { waitForExecution } from '../../utils/execution';

function Tasks({ selectedApplication }) {
  const [tasks, setTasks] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [testSuites, setTestSuites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [targetType, setTargetType] = useState('test');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    test_case_id: '',
    test_suite_id: '',
    schedule_type: 'daily',
    schedule_value: '14:00',
    enabled: true
  });
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '', type: 'info' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null });
  const [runningTasks, setRunningTasks] = useState(new Set()); // Track which tasks are currently running

  // Pagination for tasks
  const tasksPagination = usePagination(tasks, 10);

  useEffect(() => {
    if (selectedApplication) {
      loadTasks();
      loadTestCases();
      loadTestSuites();
    }
    // Deliberate: the load* helpers are rebuilt on every render, so listing
    // them here would fetch -> setState -> render -> fetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplication]);

  // Reset pagination when tasks change
  useEffect(() => {
    tasksPagination.resetPage();
    // Deliberate: keyed on the count only. The pagination object is rebuilt
    // on every render, so listing it would reset the page constantly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.length]);

  const loadTasks = async () => {
    if (!selectedApplication) {
      setTasks([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    const startTime = Date.now();
    const minLoadingTime = 2000; // 2 seconds minimum
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks?application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        setTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      // Ensure minimum 2 seconds loading time
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  };

  const loadTestCases = async () => {
    try {
      if (!selectedApplication) {
        setTestCases([]);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/test-cases?limit=1000&application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        setTestCases(data.testCases || []);
      }
    } catch (error) {
      console.error('Error loading test cases:', error);
    }
  };

  const loadTestSuites = async () => {
    try {
      if (!selectedApplication) {
        setTestSuites([]);
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/test-suites?application_id=${selectedApplication.id}`);
      const data = await response.json();
      if (data.success) {
        setTestSuites(data.testSuites || []);
      }
    } catch (error) {
      console.error('Error loading test suites:', error);
    }
  };

  const handleCreateTask = () => {
    setTargetType('test');
    setFormData({
      name: '',
      description: '',
      test_case_id: '',
      test_suite_id: '',
      schedule_type: 'daily',
      schedule_value: '14:00',
      enabled: true
    });
    setShowCreateModal(true);
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setTargetType(task.test_case_id ? 'test' : task.test_suite_id ? 'suite' : '');
    setFormData({
      name: task.name,
      description: task.description || '',
      test_case_id: task.test_case_id || '',
      test_suite_id: task.test_suite_id || '',
      schedule_type: task.schedule_type,
      schedule_value: task.schedule_value,
      enabled: task.enabled === 1
    });
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || (!formData.test_case_id && !formData.test_suite_id)) {
      setAlertModal({ isOpen: true, message: 'Please fill in all required fields', type: 'warning' });
      return;
    }

    if (!selectedApplication) {
      setAlertModal({ isOpen: true, message: 'Please select an application first', type: 'warning' });
      return;
    }

    try {
      const url = editingTask
        ? `${API_BASE_URL}/api/tasks/${editingTask.id}`
        : `${API_BASE_URL}/api/tasks`;
      
      const method = editingTask ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
        body: JSON.stringify({
          ...formData,
          application_id: selectedApplication.id,
          test_case_id: formData.test_case_id || null,
          test_suite_id: formData.test_suite_id || null
        }),
      });

      const data = await response.json();
      if (data.success) {
        loadTasks();
        setShowCreateModal(false);
        setShowEditModal(false);
        setEditingTask(null);
        setFormData({
          name: '',
          description: '',
          test_case_id: '',
          test_suite_id: '',
          schedule_type: 'daily',
          schedule_value: '14:00',
          enabled: true
        });
      } else {
        setAlertModal({ isOpen: true, message: 'Error: ' + (data.error || 'Failed to save task'), type: 'error' });
      }
    } catch (error) {
      console.error('Error saving task:', error);
      setAlertModal({ isOpen: true, message: 'Error saving task', type: 'error' });
    }
  };

  const handleDeleteTask = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    const taskName = task?.name || 'this task';
    
    setConfirmModal({
      isOpen: true,
      message: `Are you sure you want to delete "${taskName}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null, type: 'danger' });
        await performDeleteTask(taskId);
      }
    });
  };

  const performDeleteTask = async (taskId) => {

    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        loadTasks();
      } else {
        setAlertModal({ isOpen: true, message: 'Error: ' + (data.error || 'Failed to delete task'), type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting task:', error);
      setAlertModal({ isOpen: true, message: 'Error deleting task', type: 'error' });
    }
  };

  const handleExecuteTask = async (taskId) => {
    // Mark task as running
    setRunningTasks(prev => new Set(prev).add(taskId));
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${taskId}/execute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });

      const data = await response.json();
      if (data.success) {
        // The server has STARTED the run and answered - it has not finished it.
        // Waiting for the whole test on one request meant a 2 minute suite sat
        // longer than a load balancer will hold a connection open, and the page
        // reported a failure for a run that was going perfectly well.
        //
        // Follow it with the execution id instead, and keep the row marked as
        // running until it really ends.
        const result = await waitForExecution(data.executionId, {
          onTick: () => loadTasks()
        });
        loadTasks();

        if (result.status === 'error') {
          setAlertModal({ isOpen: true, message: `The run failed to start: ${result.error}`, type: 'error' });
        } else if (result.timedOut) {
          setAlertModal({ isOpen: true, message: 'Still running after 30 minutes - check Test Runs for the result.', type: 'info' });
        } else {
          setAlertModal({ isOpen: true, message: 'Run finished - see Test Runs for the result.', type: 'success' });
        }
      } else {
        setAlertModal({ isOpen: true, message: 'Error: ' + (data.error || 'Failed to execute task'), type: 'error' });
      }
    } catch (error) {
      console.error('Error executing task:', error);
      setAlertModal({ isOpen: true, message: 'Error executing task', type: 'error' });
    } finally {
      // Remove task from running set after execution completes
      setRunningTasks(prev => {
        const newSet = new Set(prev);
        newSet.delete(taskId);
        return newSet;
      });
    }
  };

  const handleToggleEnabled = async (task) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: !task.enabled
        }),
      });

      const data = await response.json();
      if (data.success) {
        loadTasks();
      }
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const getScheduleDisplay = (task) => {
    switch (task.schedule_type) {
      case 'daily':
        return `Daily at ${task.schedule_value}`;
      case 'hourly':
        return `Hourly at minute ${task.schedule_value}`;
      case 'weekly':
        return `Weekly on ${task.schedule_value}`;
      default:
        return task.schedule_value;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // The database stores two different shapes. next_run_at is a full ISO string
    // ending in Z, but last_run_at comes from SQLite's CURRENT_TIMESTAMP, which is
    // UTC with NO timezone marker. JavaScript reads a marker-less timestamp as
    // LOCAL time, so the last run was displayed 5.5 hours early here - making a
    // task that had just run look like it had not run since the morning.
    const normalized =
      typeof dateStr === 'string' && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(dateStr.trim())
        ? dateStr.trim().replace(' ', 'T') + 'Z'
        : dateStr;
    return new Date(normalized).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="tasks-container">
      <div className="tasks-section">
        <div className="section-header">
          <div>
            <h2 className="section-title">Scheduled Tasks</h2>
            <p className="section-subtitle">Manage automated test execution schedules</p>
          </div>
          <button className="btn-create-task" onClick={handleCreateTask}>
            + Create Task
          </button>
        </div>

        <div className="tasks-table-container">
          {loading ? (
            <table className="tasks-table">
              <thead>
                <tr>
                  <th>TASK NAME</th>
                  <th>STATUS</th>
                  <th>TARGET</th>
                  <th>SCHEDULE</th>
                  <th>NEXT RUN</th>
                  <th>LAST RUN</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <tr key={`shimmer-${index}`}>
                    <td colSpan="7">
                      <div style={{ padding: '1rem', background: '#F9FAFB' }}></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : tasks.length === 0 ? (
            <div className="empty-state">
              <p>No scheduled tasks found.</p>
            </div>
          ) : (
            <>
              <table className="tasks-table">
                <thead>
                  <tr>
                    <th>TASK NAME</th>
                    <th>STATUS</th>
                    <th>TARGET</th>
                    <th>SCHEDULE</th>
                    <th>NEXT RUN</th>
                    <th>LAST RUN</th>
                    <th>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {tasksPagination.currentData.map((task) => (
                    <tr key={task.id}>
                      <td>
                        <div>
                          <div className="task-name">{task.name}</div>
                          {task.description && (
                            <div className="task-description-cell">{task.description}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        {runningTasks.has(task.id) ? (
                          <span className="task-status-badge running">
                            <span className="spinner-small"></span> Running
                          </span>
                        ) : (
                          <span className={`task-status-badge ${task.enabled === 1 ? 'enabled' : 'disabled'}`}>
                            {task.enabled === 1 ? '● Enabled' : '○ Disabled'}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="task-target">
                          {task.test_case_name && `Test: ${task.test_case_name}`}
                          {task.test_suite_name && `Suite: ${task.test_suite_name}`}
                        </span>
                      </td>
                      <td>
                        <span className="task-schedule">{getScheduleDisplay(task)}</span>
                      </td>
                      <td>
                        <span className="task-next-run">{formatDate(task.next_run_at)}</span>
                      </td>
                      <td>
                        <span className="task-last-run">{task.last_run_at ? formatDate(task.last_run_at) : 'Never'}</span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn"
                            onClick={() => handleToggleEnabled(task)}
                            title={task.enabled === 1 ? 'Disable' : 'Enable'}
                          >
                            {task.enabled === 1 ? (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <rect x="4" y="4" width="8" height="8" rx="1" fill="currentColor"/>
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 3L12 7L8 11M4 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                          <button
                            className="btn-run"
                            onClick={() => handleExecuteTask(task.id)}
                            disabled={runningTasks.has(task.id)}
                            title={runningTasks.has(task.id) ? "Task is running" : "Execute Now"}
                          >
                            {runningTasks.has(task.id) ? (
                              <>
                                <span className="spinner-small"></span> Running
                              </>
                            ) : (
                              'Run'
                            )}
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => handleEditTask(task)}
                            title="Edit"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.333 2.667C11.5084 2.49127 11.7163 2.35188 11.9441 2.25699C12.1719 2.1621 12.4151 2.11377 12.6667 2.11377C12.9182 2.11377 13.1614 2.1621 13.3892 2.25699C13.617 2.35188 13.8249 2.49127 14 2.667C14.1751 2.84273 14.3145 3.05064 14.4094 3.27842C14.5043 3.5062 14.5526 3.7494 14.5526 4.00089C14.5526 4.25238 14.5043 4.49558 14.4094 4.72336C14.3145 4.95114 14.1751 5.15905 14 5.33478L5.33333 14.0014L2 14.6681L2.66667 11.3348L11.333 2.667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                          <button
                            className="action-btn"
                            onClick={() => handleDeleteTask(task.id)}
                            title="Delete"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M2 4H14M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2761 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33333 13.687 3.33333 13.3333V4M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination for tasks */}
              {tasksPagination.shouldShowPagination && (
                <Pagination
                  currentPage={tasksPagination.currentPage}
                  totalPages={tasksPagination.totalPages}
                  onPageChange={tasksPagination.handlePageChange}
                  totalItems={tasksPagination.totalItems}
                  itemsPerPage={tasksPagination.itemsPerPage}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="modal-overlay" onClick={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          setEditingTask(null);
          setTargetType('');
        }}>
          <div className="task-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTask ? 'Edit Task' : 'Create Task'}</h2>
              <button className="modal-close" onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                setEditingTask(null);
                setTargetType('');
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="task-form">
              <div className="form-group">
                <label>Task Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter task name"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter task description (optional)"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Target Type *</label>
                <CustomDropdown
                  value={targetType}
                  onChange={(e) => {
                    setTargetType(e.target.value);
                    setFormData({ ...formData, test_case_id: '', test_suite_id: '' });
                  }}
                  options={[
                    { value: '', label: '-- Select Type --' },
                    { value: 'test', label: 'Test Case' },
                    { value: 'suite', label: 'Test Suite' }
                  ]}
                  style={{ width: '100%' }}
                />
              </div>

              {targetType === 'test' && (
                <div className="form-group">
                  <label>Test Case *</label>
                  <CustomDropdown
                    value={formData.test_case_id}
                    onChange={(e) => setFormData({ ...formData, test_case_id: e.target.value, test_suite_id: '' })}
                    options={[
                      { value: '', label: '-- Select Test Case --' },
                      ...testCases.map((tc) => ({
                        value: tc.id.toString(),
                        label: tc.name
                      }))
                  ]}
                  style={{ width: '100%' }}
                  />
                </div>
              )}

              {targetType === 'suite' && (
                <div className="form-group">
                  <label>Test Suite *</label>
                  <CustomDropdown
                    value={formData.test_suite_id}
                    onChange={(e) => setFormData({ ...formData, test_suite_id: e.target.value, test_case_id: '' })}
                    options={[
                      { value: '', label: '-- Select Test Suite --' },
                      ...testSuites.map((ts) => ({
                        value: ts.id.toString(),
                        label: ts.name
                      }))
                  ]}
                  style={{ width: '100%' }}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Schedule Type *</label>
                <CustomDropdown
                  value={formData.schedule_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    let newValue = formData.schedule_value;
                    if (newType === 'daily') newValue = '14:00';
                    else if (newType === 'hourly') newValue = '0';
                    else if (newType === 'weekly') newValue = 'Monday 14:00';
                    setFormData({ ...formData, schedule_type: newType, schedule_value: newValue });
                  }}
                  options={[
                    { value: 'daily', label: 'Daily' },
                    { value: 'hourly', label: 'Hourly' },
                    { value: 'weekly', label: 'Weekly' }
                  ]}
                  style={{ width: '100%' }}
                />
              </div>

              <div className="form-group">
                <label>Schedule Value *</label>
                {formData.schedule_type === 'daily' && (
                  <input
                    type="time"
                    value={formData.schedule_value}
                    onChange={(e) => setFormData({ ...formData, schedule_value: e.target.value })}
                    required
                  />
                )}
                {formData.schedule_type === 'hourly' && (
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={formData.schedule_value}
                    onChange={(e) => setFormData({ ...formData, schedule_value: e.target.value })}
                    required
                    placeholder="Minute (0-59)"
                  />
                )}
                {formData.schedule_type === 'weekly' && (
                  <div className="weekly-schedule">
                    <select
                      value={formData.schedule_value.split(' ')[0]}
                      onChange={(e) => {
                        const time = formData.schedule_value.split(' ')[1] || '14:00';
                        setFormData({ ...formData, schedule_value: `${e.target.value} ${time}` });
                      }}
                    >
                      <option value="Monday">Monday</option>
                      <option value="Tuesday">Tuesday</option>
                      <option value="Wednesday">Wednesday</option>
                      <option value="Thursday">Thursday</option>
                      <option value="Friday">Friday</option>
                      <option value="Saturday">Saturday</option>
                      <option value="Sunday">Sunday</option>
                    </select>
                    <input
                      type="time"
                      value={formData.schedule_value.split(' ')[1] || '14:00'}
                      onChange={(e) => {
                        const day = formData.schedule_value.split(' ')[0] || 'Monday';
                        setFormData({ ...formData, schedule_value: `${day} ${e.target.value}` });
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                  <span>Enabled</span>
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setEditingTask(null);
                    setTargetType('');
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-confirm">
                  {editingTask ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ isOpen: false, message: '', type: 'info' })}
      />
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        title="Confirm Delete"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null })}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}

export default Tasks;

